import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, apiErrorMessage } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Send, Mail, MapPin, ArrowLeft, Paperclip } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export function ContactPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name || !email || !subject || !message) return;
    setSending(true);
    try {
      let attachment_file_id = null;
      if (file) {
        try {
          const form = new FormData();
          form.append("file", file);
          form.append("purpose", "contact");
          const upload = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
          attachment_file_id = upload.data?.id || null;
        } catch {
          attachment_file_id = null;
        }
      }
      await api.post("/contact", { name, email, subject, message, attachment_file_id });
      toast({ title: "Message sent!", description: "We'll get back to you soon." });
      setName(""); setEmail(""); setSubject(""); setMessage(""); setFile(null);
    } catch (err) {
      toast({ title: "Error", description: apiErrorMessage(err) });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="contact-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-30 border-b border-white/[0.06] bg-[#0a0a0a]/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <BrandLogo iconSize={52} testId="contact-logo" textClassName="text-lg font-extrabold tracking-tight text-white" />
          </button>
          <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </nav>

      {/* Hero */}
      <div className="h-[65px]" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-12 sm:pt-20 pb-6 sm:pb-10">
        <h1 data-testid="contact-hero-title" className="page-title text-3xl sm:text-5xl lg:text-6xl">
          Get in <span className="text-[#c8ff00]" style={{ textShadow: "0 0 40px rgba(200,255,0,0.3)" }}>Touch</span>
        </h1>
        <p data-testid="contact-hero-subtitle" className="mt-3 sm:mt-4 max-w-lg text-sm sm:text-base text-zinc-400 leading-relaxed">
          Have a question, partnership inquiry, or need support? We'd love to hear from you.
        </p>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pb-16 sm:pb-24">
        <div className="grid gap-8 lg:grid-cols-5">
          {/* Form — takes 3 cols */}
          <div id="contact-form" className="lg:col-span-3">
            <div className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-6 sm:p-8">
              <h2 data-testid="contact-form-title" className="text-lg font-bold">Send us a message</h2>
              <p className="mt-1 text-sm text-zinc-500">Fill out the form and we'll respond within 24 hours.</p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Name *</label>
                    <Input data-testid="contact-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required className="rounded-lg border-[#222] bg-[#111] text-white placeholder:text-zinc-600 focus:border-[#c8ff00]/40" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Email *</label>
                    <Input data-testid="contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" required className="rounded-lg border-[#222] bg-[#111] text-white placeholder:text-zinc-600 focus:border-[#c8ff00]/40" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Subject *</label>
                  <Input data-testid="contact-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What is this about?" required className="rounded-lg border-[#222] bg-[#111] text-white placeholder:text-zinc-600 focus:border-[#c8ff00]/40" />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Message *</label>
                  <Textarea data-testid="contact-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us more..." required className="min-h-[120px] rounded-lg border-[#222] bg-[#111] text-white placeholder:text-zinc-600 focus:border-[#c8ff00]/40" />
                </div>
                <div>
                  <label data-testid="contact-file-upload" className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#222] bg-[#080808] px-4 py-3 text-sm text-zinc-500 transition-all hover:border-[#c8ff00]/30 hover:bg-[#0d0d0d]">
                    <Paperclip size={14} />
                    <span>{file ? file.name : "Attach a file (optional)"}</span>
                    <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  </label>
                </div>
                <button data-testid="contact-submit" type="submit" disabled={sending || !name || !email || !subject || !message} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#c8ff00] px-6 py-3.5 text-sm font-bold text-black transition-all hover:bg-[#b8ef00] disabled:opacity-50 active:scale-[0.98]">
                  <Send size={14} /> {sending ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>

          {/* Info Cards — takes 2 cols */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#c8ff00]/10"><Mail size={18} className="text-[#c8ff00]" /></div>
                <div>
                  <div className="text-sm font-semibold">Email Us</div>
                  <div className="text-xs text-zinc-500">We'll respond within 24h</div>
                </div>
              </div>
              <a data-testid="contact-support-email" href="mailto:hello@clippingbase.com" className="block text-sm font-medium text-[#c8ff00] hover:text-[#d8ff33] transition-colors">hello@clippingbase.com</a>
            </div>

            <div className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#c8ff00]/10"><MapPin size={18} className="text-[#c8ff00]" /></div>
                <div>
                  <div className="text-sm font-semibold">Find Us At</div>
                  <div className="text-xs text-zinc-500">Stay connected</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a href="https://x.com/clippingbase?s=21" target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#222] text-zinc-400 hover:text-white hover:border-[#c8ff00]/30 transition-all"><svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg></a>
                <a href="https://www.instagram.com/clippingbase.official?igsh=ZGdwM2k5aXcxbzA2&utm_source=qr" target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#222] text-zinc-400 hover:text-white hover:border-[#c8ff00]/30 transition-all"><svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg></a>
                <a href="https://discord.gg/UPUrB8EKbT" target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#222] text-zinc-400 hover:text-white hover:border-[#c8ff00]/30 transition-all"><svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.373-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/></svg></a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#1a1a1a] px-5 sm:px-8 py-5">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 sm:flex-row">
          <div className="flex items-center gap-3 text-xs text-zinc-600">
            <BrandLogo iconSize={20} showText={false} testId="contact-footer-logo" className="flex items-center opacity-60" />
            <span>&copy; 2026 ClippingBase | All Rights Reserved</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
